import { useContext, useEffect, useState } from 'react'

import { get } from '@js/common/api'
import { useStatus } from '@js/common/hooks/useStatus'

import CTA from '@f/elements/CTA/CTA'
import Icon from '@f/elements/Icon/Icon'
import { ImmoweltLogo } from '@f/elements/PlatformLogo/PlatformLogo'

import { QuizContext } from '../context/QuizContext'
import { QuizStep } from '../partials/types'
import CheckoutTransitionInMultiPoint from './checkout/CheckoutTransitionMultiPoint'
import {
  FinanceDetailsCard,
  FinanceLeadsFoundProps,
  FinancePartner,
  RecommendedFinanceTile
} from './checkout/components/QuestionSuccessFinanceComponents'

const WAITING_FOR_LEADS_CREATION = 5000

export const QuestionSuccessFinance = ({ step }: { step: QuizStep }) => {
  const context = useContext(QuizContext)
  const [financePartners, setFinancePartners] = useState<FinancePartner[]>([])
  const [status, setStatus] = useStatus('fetching')
  const [fetchingStatus, setFetchingStatus] = useState<'fetching' | 'resolved'>(
    'fetching'
  )
  const [animationStatus, setAnimationStatus] = useState<
    'animating' | 'resolved'
  >('animating')

  useEffect(() => {
    const publicId = context.data.publicId

    if (publicId) {
      get(`public/${publicId}/finance-leads`).then(data => {
        if (!data) {
          // if no data is returned wait for WAITING_FOR_LEADS_CREATION sec and try again
          setTimeout(() => {
            get(`public/${publicId}/finance-leads`).then(secondCalldata => {
              if (!secondCalldata) {
                setFinancePartners([])
                setFetchingStatus('resolved')
                return
              }
              setFinancePartners(secondCalldata as FinancePartner[])
              setFetchingStatus('resolved')
            })
          }, WAITING_FOR_LEADS_CREATION)
        } else {
          setFinancePartners(data as FinancePartner[])
          setFetchingStatus('resolved')
        }
      })
    }
  }, [context.data.publicId, setStatus])

  useEffect(() => {
    if ([fetchingStatus, animationStatus].every(s => s === 'resolved')) {
      setStatus('resolved')
    }
  }, [fetchingStatus, animationStatus, setStatus])

  if (status === 'fetching') {
    return (
      <CheckoutTransitionInMultiPoint
        onTransitionEnd={() => {
          setAnimationStatus('resolved')
        }}
        transitionInMultiPoint={{
          title: 'Prima! 👏',
          subtitle: 'Wir werten Ihre Ergebnisse aus.',
          points: [
            'Überprüfung Ihres Finanzierungsbedarfs',
            'Berechnung Ihres idealen Finanzbudgets',
            'Auswahl der besten Finanzierungspartnern'
          ]
        }}
        transitionTimeMultiplier={1.75}
      />
    )
  }

  return (
    <div className='f-wrap f-p-b-040'>
      {status === 'resolved' && financePartners.length === 0 && (
        <FinanceLeadsNotFound />
      )}

      {status === 'resolved' && financePartners.length > 0 && (
        <FinanceLeadsFound financePartners={financePartners} />
      )}
    </div>
  )
}

const LoadingFinanceLeads = () => {
  return (
    <div className='f-wrap f-p-b-040'>
      <div className='f-pos-rel f-m-t-030'>
        <div className='spinner'></div>
      </div>
    </div>
  )
}

type CurrentStep = 'first' | 'second' | 'loading'

const FinanceLeadsFound = ({ financePartners }: FinanceLeadsFoundProps) => {
  const [currentStep, setCurrentStep] = useState<CurrentStep>('first')

  const onCTAClick = () => {
    setCurrentStep('loading')
    setTimeout(() => {
      setCurrentStep('second')
    }, 600)
  }

  return (
    <>
      {
        {
          first: <FinanceLeadsFoundStepOne onCTAClick={onCTAClick} />,
          second: (
            <FinanceLeadsFoundStepTwo financePartners={financePartners} />
          ),
          loading: <LoadingFinanceLeads />
        }[currentStep]
      }
    </>
  )
}

const FinanceLeadsFoundStepOne = ({ onCTAClick }: { onCTAClick(): void }) => {
  return (
    <>
      <div
        className='f-t-tsection f-m-a-0 f-m-t-040 f-m-b-020'
        data-sel='txt-hl'>
        Ihre Finanzierungsdetails sind bereit!
      </div>

      <div className='FinanceLeadsFound_container f-fl-gap-030'>
        <div className='f-fl-default'>
          <div className='f-t-tbody f-m-b-020 f-m-t-020 f-t-bold'>
            Hier ist Ihr berechneter Finanzierungsrahmen auf Basis Ihrer
            Angaben.
          </div>

          <FinanceDetailsCard />
        </div>

        <div className='f-fl-default'>
          <div className='f-t-tbody f-m-t-020 f-m-b-010'>
            Was sind Ihre nächsten Schritte?
          </div>
          <div className='f-d-flc f-fl-gap-010 f-fl-al-st'>
            <div className='f-d-flr'>
              <div className='f-no-shrink'>
                <Icon
                  icon='check-16px'
                  className='f-icon-025 f-c-st-suc100 f-m-r-020'
                />
              </div>
              <div className='f-t-mini'>
                Wir haben passende Finanzierungspartner für Sie gefunden, die
                Ihnen weiterhelfen können.
              </div>
            </div>
            <div className='f-d-flr'>
              <div className='f-no-shrink'>
                <Icon
                  icon='check-16px'
                  className='f-icon-025 f-c-st-suc100 f-m-r-020'
                />
              </div>
              <div className='f-t-mini'>
                Nehmen Sie Kontakt zu Ihren bevorzugten Partnern auf, um
                persönliche Beratung und maßgeschneiderte Finanzierungsangebote
                zu erhalten.
              </div>
            </div>

            <CTA
              tag='button'
              className='f-hidden:s f-m-t-020'
              type='block-primary'
              onClick={() => onCTAClick()}>
              Zu den Finanzierungspartnern
            </CTA>
          </div>
        </div>
      </div>
      <div>
        <p className='f-t-tbody'>
          Dieser Service wird Ihnen in Partnerschaft mit Immowelt bereitgestellt
        </p>
        <p className='f-t-left'>
          <ImmoweltLogo />
        </p>
      </div>

      <div className='FinanceLeadsFound_cta-wrapper'>
        <div className='FinanceLeadsFound_cta-container'>
          <CTA
            tag='button'
            className='f-hidden:m f-hidden:l'
            type='block-primary'
            onClick={() => onCTAClick()}>
            Zu den Finanzierungspartnern
          </CTA>
        </div>
      </div>
    </>
  )
}

const FinanceLeadsFoundStepTwo = ({
  financePartners
}: FinanceLeadsFoundProps) => {
  return (
    <>
      <div className='f-t-tsection f-m-a-0 f-m-t-040 f-m-b-040'>
        Ihre empfohlenen Finanzierungspartner
      </div>

      <div className='f-t-tbody f-t-bold' style={{ maxWidth: '600px' }}>
        Erhalten Sie individuelle Beratung und maßgeschneiderte
        Finanzierungslösungen von unseren vertrauenswürdigen Partnern.
      </div>
      <div className='f-d-flc f-fl-gap-010 f-m-t-010'>
        <div className='f-d-flr f-fl-al-ce'>
          <div className='f-no-shrink'>
            <Icon
              icon='check-16px'
              className='f-icon-025 f-c-st-suc100 f-m-r-020'
            />
          </div>
          <div className='f-t-mini'>Maßgeschneiderte Finanzierungslösungen</div>
        </div>

        <div className='f-d-flr f-fl-al-ce'>
          <div className='f-no-shrink'>
            <Icon
              icon='check-16px'
              className='f-icon-025 f-c-st-suc100 f-m-r-020'
            />
          </div>
          <div className='f-t-mini'>
            Beratung, die kostenlos und unverbindlich ist
          </div>
        </div>

        <div className='f-d-flr f-fl-al-ce'>
          <div className='f-no-shrink'>
            <Icon
              icon='check-16px'
              className='f-icon-025 f-c-st-suc100 f-m-r-020'
            />
          </div>
          <div className='f-t-mini'>
            Verlässliche Partner mit geprüfter Expertise
          </div>
        </div>
      </div>

      <div className='f-t-tbody f-m-v-020'>
        Diese Finanzierungspartner stehen bereit, um Sie zu unterstützen:
      </div>

      <div className='RecommendedFinance_container f-m-t-010'>
        {financePartners.map(financePartner => {
          return (
            <RecommendedFinanceTile
              key={financePartner.uuid}
              data={financePartner}
            />
          )
        })}
      </div>

      <div>
        <p className='f-t-tbody' style={{ maxWidth: '600px' }}>
          Jetzt den nächsten Schritt machen und die besten Finanzierungsangebote
          sichern – kontaktieren Sie einen unserer Partner!
        </p>
        <p className='f-t-left'>
          <ImmoweltLogo />
        </p>
      </div>
    </>
  )
}

const FinanceLeadsNotFound = () => {
  return (
    <>
      <div
        className='f-t-tsection f-m-a-0 f-m-t-040 f-m-b-020'
        data-sel='txt-hl'>
        Ihre Finanzierungsdetails sind bereit!
      </div>

      <div className='FinanceLeadsFound_container f-fl-gap-030'>
        <div className='f-fl-default'>
          <div className='f-t-tbody f-m-v-020 f-t-bold'>
            Hier ist Ihr berechneter Finanzierungsrahmen auf Basis Ihrer
            Angaben.
          </div>

          <FinanceDetailsCard />
        </div>

        <div className='f-fl-default'>
          <div className='f-t-tbody f-m-t-020 f-m-b-010'>
            Was sind Ihre nächsten Schritte?
          </div>

          <div className='f-t-mini f-m-b-020'>
            Leider konnten wir keine passenden Finanzierungspartner für Ihre
            Kriterien finden. Nutzen Sie die folgenden Hinweise, um Ihre
            nächsten Schritte zu planen:
          </div>
          <div className='f-d-flc f-fl-gap-010 f-fl-al-st'>
            <div className='f-d-flr'>
              <div className='f-no-shrink'>
                <Icon
                  icon='check-16px'
                  className='f-icon-025 f-c-st-suc100 f-m-r-020'
                />
              </div>
              <div className='f-t-mini'>
                Verwenden Sie unseren berechneten Finanzierungsrahmen als
                Orientierung.
              </div>
            </div>
            <div className='f-d-flr'>
              <div className='f-no-shrink'>
                <Icon
                  icon='check-16px'
                  className='f-icon-025 f-c-st-suc100 f-m-r-020'
                />
              </div>
              <div className='f-t-mini'>
                Informieren Sie sich bei Banken oder unabhängigen
                Finanzberatern.
              </div>
            </div>
            <div className='f-d-flr'>
              <div className='f-no-shrink'>
                <Icon
                  icon='check-16px'
                  className='f-icon-025 f-c-st-suc100 f-m-r-020'
                />
              </div>
              <div className='f-t-mini'>
                Passen Sie Ihre Kriterien an, um weitere Optionen zu prüfen.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <p className='f-t-tbody'>
          Dieser Service wird Ihnen in Partnerschaft mit Immowelt bereitgestellt
        </p>
        <p className='f-t-left'>
          <ImmoweltLogo />
        </p>
      </div>
    </>
  )
}
